import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientSaveEstateStatusHouseholdSettings, useGraphqlClient } from '../../graphql';
import { scorecardKeys } from '../../Scorecard/queryKeys';
import { ESTATE_STATUS_PERCENT_SCALE } from '../constants';
import { estateStatusKeys } from '../queryKeys';
import { calculateEstateStatusSummary, cloneEstateStatusChecklist } from '../util';
import type { EstateStatus, SaveEstateStatusChecklistInput } from '../types';

function setScorecardEstateStatusCache(
  queryClient: ReturnType<typeof useQueryClient>,
  householdID: string | undefined | null,
  score: number | null | undefined
) {
  queryClient.setQueriesData({ queryKey: scorecardKeys.scorecard(householdID) }, (scorecard: unknown) => {
    if (!scorecard || typeof scorecard !== 'object') {
      return scorecard;
    }

    return {
      ...scorecard,
      es: score == null ? null : score / ESTATE_STATUS_PERCENT_SCALE,
    };
  });
}

export function useSaveEstateStatusHouseholdSettings(householdID: string | undefined | null) {
  const gqlClient = useGraphqlClient();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (input: SaveEstateStatusChecklistInput) => {
      if (!householdID) {
        return Promise.reject(new Error('Household ID is required to save Estate Status.'));
      }

      return clientSaveEstateStatusHouseholdSettings(gqlClient, { householdID, ...input });
    },
    {
      onMutate: async (input) => {
        if (!householdID) {
          return { previousEstateStatus: undefined };
        }

        const queryKey = estateStatusKeys.householdSettings(householdID);
        const previousEstateStatus = queryClient.getQueryData<EstateStatus>(queryKey);

        if (!previousEstateStatus) {
          return { previousEstateStatus };
        }

        const optimisticChecklist = cloneEstateStatusChecklist(previousEstateStatus.checklist).map((item) => {
          const savedItem = input.checklist.find((checklistItem) => checklistItem.id === item.id);

          if (!savedItem) {
            return item;
          }

          const optimisticAttachmentLinks = (savedItem.attachmentLinks ?? []).map((attachmentLink) => {
            const existingAttachmentLink = item.attachmentLinks.find(
              (currentAttachmentLink) => currentAttachmentLink.id === attachmentLink.id
            );

            return {
              ...existingAttachmentLink,
              id: attachmentLink.id,
              fileName: attachmentLink.fileName,
              url: attachmentLink.url,
              uploadedAt: existingAttachmentLink?.uploadedAt ?? null,
              uploadedByUserID: existingAttachmentLink?.uploadedByUserID ?? '',
            };
          });

          return {
            ...item,
            state: savedItem.state,
            lastReviewedAt: savedItem.lastReviewedAt ?? null,
            note: savedItem.note ?? '',
            attachmentLinks: optimisticAttachmentLinks,
          };
        });
        const optimisticSummary = calculateEstateStatusSummary(optimisticChecklist);
        const optimisticEstateStatus = {
          ...previousEstateStatus,
          checklist: optimisticChecklist,
          score: optimisticSummary.score,
          summary: optimisticSummary,
        };

        queryClient.setQueryData(queryKey, optimisticEstateStatus);
        setScorecardEstateStatusCache(queryClient, householdID, optimisticSummary.score);

        return { previousEstateStatus };
      },
      onError: (_error, _input, context) => {
        if (!householdID) {
          return;
        }

        if (!context?.previousEstateStatus) {
          return;
        }

        queryClient.setQueryData(estateStatusKeys.householdSettings(householdID), context.previousEstateStatus);
        setScorecardEstateStatusCache(queryClient, householdID, context.previousEstateStatus.score);
      },
      onSuccess: async (savedEstateStatus) => {
        if (!householdID) {
          return;
        }

        if (savedEstateStatus) {
          queryClient.setQueryData(estateStatusKeys.householdSettings(householdID), savedEstateStatus);
        }

        if (typeof savedEstateStatus?.score === 'number' || savedEstateStatus?.score === null) {
          // Estate Status is not date-versioned, so update every cached scorecard variant for the household.
          setScorecardEstateStatusCache(queryClient, householdID, savedEstateStatus.score);
        }

        await queryClient.invalidateQueries({ queryKey: estateStatusKeys.householdSettings(householdID) });
        await queryClient.invalidateQueries({ queryKey: scorecardKeys.scorecard(householdID) });
      },
    }
  );

  return { saveEstateStatusHouseholdSettingsMutation: mutation };
}
